import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Product, ProductVariant } from '@/types/product-type';
import { Minus, Pin, PinOff, Plus } from 'lucide-react';
import React from 'react';
import { EditPriceButton } from './edit-price-button';
import { ProductSaleProgress } from './product-sale-progress';
import { ProductSaleStockBadge } from './product-sale-stock-badge';
import { ProductThumbnailStatic } from './product-thumbnail';
import { SkuCopyButton } from './sku-copy-button';

export type ProductSaleListCardProps = {
  product: Product;
  variant: ProductVariant;
  variantId: string;
  remain: number;
  sold: number;
  total: number;
  isSelected: boolean;
  isPinned: boolean;
  onToggleSelect: (id: string) => void;
  onTogglePin?: (id: string, productId: string, pinned: boolean) => void;
  onRefresh?: (id: string) => void;
  onAdjustIn?: (variant: ProductVariant) => void;
  onAdjustOut?: (variant: ProductVariant) => void;
  onEditPrice?: (variant: ProductVariant) => void;
  priority?: boolean;
};

const actionButtonClass =
  'h-7 w-7 shrink-0 rounded-md border-slate-200 sm:h-8 sm:w-8';

function ProductSaleListCardComponent({
  product: p,
  variant: v,
  variantId,
  remain,
  sold,
  isSelected,
  isPinned,
  onTogglePin,
  onAdjustIn,
  onAdjustOut,
  onEditPrice,
  priority = false,
}: ProductSaleListCardProps) {
  const low = remain > 0 && remain <= 3;
  const out = remain === 0;
  const sku = v.sku ?? p.code;
  const variantName = v.name || v.productName || p.name;
  const price = (v.price ?? p.price ?? 0).toFixed(2);
  const imageUrl = v.imageUrl || p.imageUrl;

  return (
    <Card
      className={cn(
        'border border-slate-200/90 bg-white shadow-none',
        'hover:border-slate-300',
        isSelected && 'border-blue-200 ring-2 ring-blue-500/25',
        isPinned && 'border-amber-200/80 bg-amber-50/20',
        out && 'opacity-75',
      )}
    >
      <div className="flex items-center gap-2 p-2 sm:gap-3 sm:p-2.5">
        <div className="relative shrink-0">
          <ProductThumbnailStatic
            imageUrl={imageUrl}
            alt={variantName}
            size="md"
            borderStyle="none"
            priority={priority}
            className="rounded-md border border-slate-100 bg-slate-50 sm:h-16 sm:w-16"
          />

          <Button
            variant="outline"
            size="icon"
            className={cn(
              actionButtonClass,
              'absolute -right-1 -top-1 z-10 bg-white/95 shadow-sm',
              isPinned && 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
            )}
            onClick={() => onTogglePin?.(variantId, p.id, !isPinned)}
            onPointerDown={(e) => e.stopPropagation()}
            title={isPinned ? 'Unpin variant' : 'Pin variant to tabs'}
          >
            {isPinned ? <PinOff className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <Pin className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
          </Button>

          <ProductSaleProgress
            sold={sold}
            total={remain}
            out={out}
            compact
            className="mt-1 hidden min-w-[3.5rem] sm:block"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex min-w-0 items-center gap-1.5">
            <SkuCopyButton sku={sku ?? ''} />
            <ProductSaleStockBadge remain={remain} low={low} out={out} variant="compact" />
          </div>

          <p className="break-words text-xs font-medium leading-snug text-slate-900 sm:text-sm">{variantName}</p>

          <ProductSaleProgress
            sold={sold}
            total={remain}
            out={out}
            compact
            showLabel={false}
            className="mt-1 sm:hidden"
          />
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
          <EditPriceButton
            price={price}
            onClick={() => onEditPrice?.(v)}
            className="h-7 px-2.5 text-[11px] sm:text-xs"
          />

          <div className="flex items-center gap-0.5 sm:gap-1">
            <Button
              variant="outline"
              size="icon"
              className={cn(
                actionButtonClass,
                'hover:border-red-200 hover:bg-red-50 hover:text-red-600',
              )}
              onClick={() => onAdjustOut?.(v)}
              title="Decrease stock (-1)"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>

            <span
              className={cn(
                'min-w-[2rem] text-center text-[11px] font-semibold tabular-nums sm:text-xs',
                out ? 'text-red-600' : low ? 'text-amber-700' : 'text-slate-600',
              )}
              title={`${remain} in stock · ${sold} sold`}
            >
              {sold}/{remain}
            </span>

            <Button
              variant="outline"
              size="icon"
              className={cn(
                actionButtonClass,
                'hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600',
              )}
              onClick={() => onAdjustIn?.(v)}
              title="Increase stock (+1)"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function listCardPropsEqual(prev: ProductSaleListCardProps, next: ProductSaleListCardProps) {
  return (
    prev.variantId === next.variantId &&
    prev.sold === next.sold &&
    prev.remain === next.remain &&
    prev.isSelected === next.isSelected &&
    prev.isPinned === next.isPinned &&
    prev.variant.price === next.variant.price &&
    prev.variant.imageUrl === next.variant.imageUrl &&
    prev.variant.sku === next.variant.sku &&
    prev.variant.name === next.variant.name &&
    prev.product.name === next.product.name
  );
}

export const ProductSaleListCard = React.memo(ProductSaleListCardComponent, listCardPropsEqual);
